import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Loader from '../common/Loader';
import {
  loadProjects, getUserPreferences, updateUserPreferences, addProject,
} from '../../redux/actions/projectsActions';

import { MAIN_UI_COLOR } from '../../config';
import RyffineDialogTitle from '../common/RyffineDialogTitle';
import { setActiveProjectId } from '../../core/services/authService';

function ProjectsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const projects = useSelector(state => state.projects.projects);
  const activeProjectId = useSelector(state => state.projects.activeProject?.id);

  const [selectedProjects, setSelectedProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [isAccordionOpen, setAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);

      dispatch(getUserPreferences());
      dispatch(loadProjects()).finally(() => setLoading(false));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (activeProjectId) {
      setSelectedProjects([activeProjectId]);
    }
  }, [activeProjectId]);

  const handleToggle = (id, name) => {
    if (selectedProjects.includes(id)) {
      setSelectedProjects([]);
      setActiveProjectId(undefined);

      dispatch(updateUserPreferences('', null));
    } else {
      setSelectedProjects([id]);
      setActiveProjectId(id);

      dispatch(updateUserPreferences(id, name));
    }
  };

  const handleAddProject = () => {
    if (newProjectName.trim()) {
      dispatch(addProject(newProjectName));
      setNewProjectName('');
      setAccordionOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <RyffineDialogTitle onClose={onClose}>
        Projects List
      </RyffineDialogTitle>
      <DialogContent style={{ paddingBottom: '20px', backgroundColor: 'rgb(248, 248, 248)' }}>
        {loading && <Loader />}
        {!loading && projects.length > 0 && (
        <List>
          {projects.map(project => (
            <ListItem
              key={project.id}
              divider
              button
              onClick={() => handleToggle(project.id, project.name)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={selectedProjects.includes(project.id)}
                  style={{ color: MAIN_UI_COLOR }}
                />
              </ListItemIcon>
              <ListItemText
                primary={project.name}
                secondary={`Created: ${new Date(project.created_at).toLocaleString()}`}
              />
            </ListItem>
          ))}
        </List>
        )}
        {!loading && projects.length === 0 && <p>No projects available.</p>}
        <Accordion expanded={isAccordionOpen} onChange={() => setAccordionOpen(!isAccordionOpen)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Add New Project</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              label="Project Name"
              variant="outlined"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions style={{ padding: 0 }}>
        <Grid
          container
          style={{
            padding: '16px 24px 16px 24px',
            background: MAIN_UI_COLOR,
          }}
        >
          <Button
            className="button"
            style={{
              background: 'rgb(248, 248, 248)',
              minWidth: '110px',
            }}
            onClick={handleAddProject}
            disabled={!newProjectName.trim()}
            variant="outlined"
            color="primary"
          >
            Add Project
          </Button>
          <Button
            style={{ marginLeft: '10px', color: '#ffffff' }}
            onClick={onClose}
            className="button"
          >
            Close
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

ProjectsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectsDialog;
